<template>
    <section v-if="isRenderable" class="station-map station-locator">
        <div class="inner">
            <GoogleMap
                v-if="coordinates.lat && coordinates.lon"
                class="map"
                :api-key="gmapsApiKey"
                v-bind="gmapConfig"
                map-id="station-map"
                map-type-id="roadmap"
            >
                <GoogleMapAdvancedMarker
                    :options="{
                        collisionBehavior: 'OPTIONAL_AND_HIDES_LOWER_PRIORITY',
                        position: { lat: Number(coordinates.lat), lng: Number(coordinates.lon) },
                        content: stationPin(),
                    }"
                />
            </GoogleMap>
        </div>
    </section>
</template>
<script setup>
import { useRuntimeConfig, computed } from '#imports';

const config = useRuntimeConfig();
const { apikey: gmapsApiKey } = { ...config.public.locator };

const props = defineProps({
    stations: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const stationPin = () => {
    if (!import.meta.client) return null;

    const pin = document.createElement('div');
    pin.classList.add('station-pin');
    const image = document.createElement('img');
    const title = document.createElement('span');
    title.classList.add('title');
    title.innerHTML = props.stations.value.title;
    image.src = resolveMarkerImage(props.stations?.value?.brand);
    pin.appendChild(image);
    pin.appendChild(title);

    return pin;
};

const coordinates = computed(() => props.stations?.value?.location);
const hasValidCoordinates = computed(() => {
    const lat = parseFloat(coordinates.value?.lat);
    const lng = parseFloat(coordinates.value?.lon);

    return !isNaN(lat) && !isNaN(lng);
});
const isRenderable = computed(() => gmapsApiKey !== undefined && hasValidCoordinates.value);
const position = computed(() => ({ lat: Number(coordinates.value?.lat), lng: Number(coordinates.value?.lon) }));
const gmapConfig = computed(() => ({
    backgroundColor: '#fff',
    disableDefaultUI: true,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
    noClear: true,
    streetViewControl: false,
    gestureHandling: 'greedy',
    clickableIcons: false,
    scrollwheel: false,
    center: position.value,
    zoom: 13,
}));

const resolveMarkerImage = brand => {
    const lcBrand = brand.toLowerCase();
    if (lcBrand === 'ploq') {
        return '/images/ploq-logo.png';
    } else if (lcBrand === 'shell') {
        return '/images/shell-logo.png';
    }

    return '/images/st1-logo.webp';
};
</script>
<script>
export default {
    name: 'StationMap',
};
</script>
<style scoped>
.station-map {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.station-map .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}

.marker.container {
  padding-bottom: 0.25rem;
  margin-bottom: -0.25rem;
  display: inline-flex;
  overflow: visible;
}
.marker.container .content {
  padding: 0.25rem;
  border-radius: 0.25rem;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
  background: var(--color-base-white);
  font-weight: 600;
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;
}
.marker.container .content .logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.marker.container .content .title {
  transition: all 0.5s ease;
  white-space: nowrap;
  opacity: 0;
  max-width: 0;
}
.marker.container .content::after {
  content: "";
  position: absolute;
  left: 0.875rem;
  bottom: 0;
  width: 0.5rem;
  height: 0.5rem;
  transform: translate(-50%, 50%) rotateZ(45deg);
  background: linear-gradient(-45deg, var(--color-base-white) 0 50%, transparent 50% 100%);
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);
}

@media (min-width: 1200px) {
  .map {
    height: 26.25rem;
  }
}
@media (max-width: 1199px) {
  .station-map .inner {
    padding-inline: 0;
  }
  .map {
    height: 30rem;
  }
}
</style>
